import { authService } from "../utils/authService";
import { errorHandler } from "../components/shared/errorhandler";

// ============================================================================
// 2-CALL AUTH FLOW
// ============================================================================

export interface SessionResponse {
  isSuccess: boolean;
  user: {
    id: number;
    email: string;
    displayName: string | null;
    azureB2CId: string | null;
    status: string;
  };
  instanceId: string | null;
  isB2CAdmin: boolean;
}

export interface ResolveInstanceResponse {
  hasAccess: boolean;
  role: string | null;
  permissions: Record<string, boolean>;
  navigation: Record<string, boolean>;
  source?: string;
  reason?: string;
  /**
   * True when the user holds a `skypoint_admin` role assignment at ANY scope.
   * Mirrors the backend requireSkypointAdmin gate — NOT derived from `role`.
   */
  isSkypointAdmin?: boolean;
}

/**
 * POST /api/user/session — Call 1 of 2
 * Validates B2C token, upserts user, returns profile + default instanceId.
 */
const createAuthSession = async (): Promise<SessionResponse> => {
  return fetch("/api/user/session", {
    method: "POST",
    headers: await authService.getHeader(),
    body: JSON.stringify({}),
  })
    .then(errorHandler)
    .then((response) => response.json());
};

/**
 * POST /api/user/resolve-instance — Call 2 of 2
 * Given instanceId, returns { hasAccess, role, permissions, navigation }.
 */
export const resolveInstance = async (instanceId: string): Promise<ResolveInstanceResponse> => {
  return fetch("/api/user/resolve-instance", {
    method: "POST",
    headers: await authService.getHeader(),
    body: JSON.stringify({ instanceId }),
  })
    .then(errorHandler)
    .then((response) => response.json());
};

// ============================================================================
// SUPPORTING ENDPOINTS
// ============================================================================

/**
 * Fetch instance IDs that are provisioned (schema exists) in our local database.
 */
export const fetchProvisionedInstanceIds = async (): Promise<string[]> => {
  const res = await fetch("/api/v1/instance/provisioned", {
    headers: await authService.getAuthorizationHeader(),
  });
  if (!res.ok) return [];
  const data: { instanceIds: string[] } = await res.json();
  return data.instanceIds;
};

/**
 * Fetch instance IDs that the current user has access to (via /api/v1/admin/instances/my).
 */
export const fetchAccessibleInstanceIds = async (): Promise<string[]> => {
  const res = await fetch("/api/v1/admin/instances/my", {
    headers: await authService.getAuthorizationHeader(),
  });
  if (!res.ok) {
    throw new Error(`instances/my failed with status ${res.status}`);
  }
  const data = await res.json();
  const ids: string[] = [];
  for (const tenant of data.tenants || []) {
    for (const inst of tenant.instances || []) {
      if (inst.id) ids.push(inst.id);
    }
  }
  return ids;
};
